import { CSSProperties, ReactNode, TouchEvent, useEffect, useRef, useState } from 'react';
import { registerModalBackHandler } from '../../../lib/native';
import { viewportHeight } from '../../../lib/viewport';

export interface BottomSheetProps {
  title?: string;
  /** Accessible name for the dialog when the caller renders its own heading
   *  inside `children` instead of using `title` (e.g. a custom font size) —
   *  defaults to `title` when omitted. */
  ariaLabel?: string;
  onClose: () => void;
  /** True while the caller is animating this sheet out — it keeps
   *  rendering (with its last content) and plays the mirror of the open
   *  animation instead of just vanishing. The caller is responsible for
   *  actually unmounting once that animation has had time to finish
   *  (matching TRANSITION_MS below); this prop alone doesn't unmount
   *  anything. */
  closing?: boolean;
  style?: CSSProperties;
  children: ReactNode;
}

/** How long the open/close slide takes — exported so callers can time the
 *  delay before they actually remove this from the tree after setting
 *  `closing`. */
export const BOTTOM_SHEET_TRANSITION_MS = 340;

// UIKit's own sheet-presentation spring — a soft settle rather than a flat
// deceleration. Used for every motion this sheet makes (open, parent-driven
// close, drag-cancel spring-back, drag-commit close) so they all read as
// the same gesture regardless of what triggered them.
const SPRING = 'cubic-bezier(.32,.72,0,1)';
const DISMISS_THRESHOLD = 100;

/**
 * BottomSheet — a generic backdrop + slide-up panel for "more options"
 * style menus (as opposed to PlaceSheet, which is content-specific). Rises
 * from the bottom with a drag-handle affordance and an optional title,
 * mirrors that rise as a slide-down when `closing` is set, and can also be
 * dragged down by hand to dismiss — like a native sheet, only once the
 * content is scrolled to its top (otherwise the drag just scrolls it).
 *
 * Motion is driven by `transition`, not `@keyframes`: a live drag mutates
 * `panelRef`/`backdropRef` directly (no per-frame re-render), and CSS
 * transitions only animate when a value actually changes — so a re-render
 * that happens to declare the same resting position mid-drag-release is a
 * no-op instead of restarting the motion from scratch, which is exactly
 * what a `@keyframes` animation would do if replayed at the wrong moment.
 */
export function BottomSheet({ title, ariaLabel, onClose, closing = false, style, children }: BottomSheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [forceClosed, setForceClosed] = useState(false);
  const gesture = useRef<{ y: number; scrollTop: number; active: boolean } | null>(null);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Android hardware back button closes this sheet instead of navigating
  // the route underneath it.
  useEffect(() => {
    if (closing || forceClosed) return;
    return registerModalBackHandler(onClose);
  }, [closing, forceClosed, onClose]);

  function onTouchStart(e: TouchEvent) {
    if (closing || forceClosed) return;
    const t = e.touches[0];
    gesture.current = { y: t.clientY, scrollTop: panelRef.current?.scrollTop ?? 0, active: false };
  }

  function onTouchMove(e: TouchEvent) {
    const g = gesture.current;
    if (!g) return;
    const t = e.touches[0];
    const dy = t.clientY - g.y;
    if (!g.active) {
      // Only takes over once the sheet's own content is already scrolled
      // to the top and the drag is downward — otherwise this is just a
      // normal scroll of the sheet's content, same as native iOS sheets.
      if (g.scrollTop > 0 || dy <= 6) return;
      g.active = true;
      setDragging(true);
      if (panelRef.current) panelRef.current.style.transition = 'none';
      if (backdropRef.current) backdropRef.current.style.transition = 'none';
    }
    const dyClamped = Math.max(0, dy);
    if (panelRef.current) panelRef.current.style.transform = `translateY(${dyClamped}px)`;
    if (backdropRef.current) backdropRef.current.style.opacity = String(Math.max(0.15, 1 - dyClamped / 400));
  }

  function onTouchEnd(e: TouchEvent) {
    const g = gesture.current;
    gesture.current = null;
    if (!g?.active) return;
    const t = e.changedTouches[0];
    const dy = t ? Math.max(0, t.clientY - g.y) : 0;
    const commit = dy > DISMISS_THRESHOLD;
    if (panelRef.current) {
      panelRef.current.style.transition = `transform ${BOTTOM_SHEET_TRANSITION_MS}ms ${SPRING}`;
      panelRef.current.style.transform = commit ? 'translateY(100%)' : 'translateY(0)';
    }
    if (backdropRef.current) {
      backdropRef.current.style.transition = `opacity ${BOTTOM_SHEET_TRANSITION_MS}ms ${SPRING}`;
      backdropRef.current.style.opacity = commit ? '0' : '1';
    }
    // Batched with the ref mutations above into one re-render, which then
    // declares these exact same resting values — so it lands as a no-op
    // rather than interrupting the transition already under way.
    setDragging(false);
    if (commit) {
      setForceClosed(true);
      setTimeout(onClose, BOTTOM_SHEET_TRANSITION_MS);
    }
  }

  const panelClosed = closing || forceClosed || !open;
  const backdropOpacity = open && !closing && !forceClosed ? 1 : 0;
  // Built as the full `transition` value per property, not interpolated as
  // `` `transform ${motion}` `` — when `motion` is the bare string 'none',
  // that interpolation produces the invalid declaration
  // `transition: transform none`, which browsers silently drop rather than
  // apply, leaving whatever transition was last valid still in effect.
  const panelTransition = dragging ? 'none' : `transform ${BOTTOM_SHEET_TRANSITION_MS}ms ${SPRING}`;
  const backdropTransition = dragging ? 'none' : `opacity ${BOTTOM_SHEET_TRANSITION_MS}ms ${SPRING}`;

  return (
    <>
      <div
        ref={backdropRef}
        onClick={closing || forceClosed ? undefined : onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 60,
          opacity: backdropOpacity,
          transition: backdropTransition,
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-label={ariaLabel ?? title}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, margin: '0 auto',
          width: '100%', maxWidth: 480, zIndex: 61,
          background: 'var(--surface)', borderRadius: '24px 24px 0 0',
          maxHeight: viewportHeight(0.85), overflowY: 'auto', overscrollBehavior: 'contain', fontFamily: 'var(--font-sans)',
          transform: panelClosed ? 'translateY(100%)' : 'translateY(0)',
          transition: panelTransition,
          paddingBottom: 'max(20px, var(--sab))',
          ...style,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
          <span style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--line)' }} />
        </div>
        {title && (
          <div style={{ padding: '14px 20px 4px' }}>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'calc(19px * var(--text-scale))', fontWeight: 400, color: 'var(--text)' }}>{title}</h2>
          </div>
        )}
        <div style={{ padding: '8px 12px 4px' }}>{children}</div>
      </div>
    </>
  );
}

export default BottomSheet;
